/**
 * Agent loadout generation — rolls a procedural set of variables (weapon,
 * defensive items, passives, face, codename, deck archetype) based on the
 * current run round. Called once per round by initializeGameState().
 *
 * Scaling philosophy: rounds 1-2 should feel like scouting/stalking
 * threats, rounds 3-4 firearms become the norm, round 5+ agents are fully
 * kitted with passives and backup gear. Sniper unlocks at round 3+ with
 * overwatch AI behavior (Phase 3).
 */

import type { AgentArchetype, AgentLoadout, AgentPassive } from '../types/game';
import {
  ALL_AGENT_PASSIVE_KEYS,
  SOFT_AGENT_PASSIVE_KEYS,
  getAgentPassive,
} from '../data/agentPassives';

// --- Weighted roll helper ---

type Weighted<T> = { value: T; weight: number };

function rollWeighted<T>(options: Weighted<T>[]): T {
  const total = options.reduce((sum, o) => sum + o.weight, 0);
  let roll = Math.random() * total;
  for (const o of options) {
    roll -= o.weight;
    if (roll <= 0) return o.value;
  }
  return options[options.length - 1].value;
}

// --- Weapon roll ---

/**
 * Weapon scaling by round. Sniper stays at 0% for every bucket until
 * sniper AI behavior is implemented. When we add it, the pool for round
 * 3+ should get a 10-40% sniper slice taken from the pistol share.
 */
function rollWeapon(round: number): AgentLoadout['weaponId'] {
  if (round <= 1) {
    return rollWeighted<AgentLoadout['weaponId']>([
      { value: null,           weight: 60 }, // unarmed
      { value: 'combat-knife', weight: 40 },
    ]);
  }
  if (round === 2) {
    return rollWeighted<AgentLoadout['weaponId']>([
      { value: null,           weight: 30 },
      { value: 'combat-knife', weight: 50 },
      { value: 'pistol',       weight: 20 },
    ]);
  }
  if (round === 3) {
    return rollWeighted<AgentLoadout['weaponId']>([
      { value: null,           weight: 10 },
      { value: 'combat-knife', weight: 30 },
      { value: 'pistol',       weight: 45 },
      { value: 'sniper-rifle', weight: 15 },
    ]);
  }
  if (round === 4) {
    return rollWeighted<AgentLoadout['weaponId']>([
      { value: null,           weight: 5  },
      { value: 'combat-knife', weight: 15 },
      { value: 'pistol',       weight: 55 },
      { value: 'sniper-rifle', weight: 25 },
    ]);
  }
  // round >= 5
  return rollWeighted<AgentLoadout['weaponId']>([
    { value: 'combat-knife',  weight: 10 },
    { value: 'pistol',        weight: 60 },
    { value: 'sniper-rifle',  weight: 30 },
  ]);
}

// --- Defensive items ---

function rollDefensiveItems(round: number): string[] {
  if (round <= 1) return [];

  // Target count per round
  const targetCount =
    round === 2 ? (Math.random() < 0.5 ? 0 : 1) :
    round === 3 ? 1 :
    round === 4 ? (Math.random() < 0.4 ? 2 : 1) :
    /* round >= 5 */ (Math.random() < 0.6 ? 2 : 1);

  if (targetCount === 0) return [];

  const pool: Weighted<string>[] =
    round <= 3
      ? [
          { value: 'flashbang',       weight: 70 },
          { value: 'bulletproof-vest', weight: 30 },
        ]
      : [
          // Round 4+ biases toward vests — tougher to kill
          { value: 'flashbang',       weight: 50 },
          { value: 'bulletproof-vest', weight: 50 },
        ];

  // Roll with de-duplication so we don't hand the same agent two vests.
  const picked: string[] = [];
  while (picked.length < targetCount) {
    const remaining = pool.filter(p => !picked.includes(p.value));
    if (remaining.length === 0) break;
    picked.push(rollWeighted(remaining));
  }
  return picked;
}

// --- Passives ---

/**
 * Roll N passives from the round-appropriate pool. The pool varies with
 * round tier (round 2 = soft-only, 3-4 = full pool, 5+ = full + scouting-
 * nerf bias). Caller picks the count — use this from rollPassives (auto
 * count per round) or directly when the map node dictates a count (Hit
 * nodes = 0, Elite/Boss nodes = N regardless of round).
 */
function pickPassivesFromPool(round: number, count: number): AgentPassive[] {
  if (count <= 0) return [];
  let pool: AgentPassive['key'][];
  if (round <= 2) {
    pool = [...SOFT_AGENT_PASSIVE_KEYS];
  } else if (round >= 5) {
    // 2x weight on scouting nerfs
    pool = [
      ...ALL_AGENT_PASSIVE_KEYS,
      'eagleEye',
      'deepCover',
    ];
  } else {
    pool = [...ALL_AGENT_PASSIVE_KEYS];
  }
  const picked: AgentPassive['key'][] = [];
  while (picked.length < count && pool.length > 0) {
    const idx = Math.floor(Math.random() * pool.length);
    const key = pool[idx];
    if (!picked.includes(key)) picked.push(key);
    pool.splice(idx, 1);
  }
  return picked.map(getAgentPassive);
}

function rollPassives(round: number): AgentPassive[] {
  if (round <= 1) return [];
  const count =
    round === 2 ? (Math.random() < 0.5 ? 0 : 1) :
    round === 3 ? 1 :
    round === 4 ? (Math.random() < 0.5 ? 2 : 1) :
    /* round >= 5 */ 2;
  return pickPassivesFromPool(round, count);
}

// --- Archetype ---

function rollArchetype(round: number): AgentArchetype {
  // Round 1 is always tracker — consistent easy opener.
  if (round <= 1) return 'tracker';
  // Round 2 mixes tracker + enforcer (commander).
  if (round === 2) {
    return rollWeighted<AgentArchetype>([
      { value: 'tracker',   weight: 60 },
      { value: 'commander', weight: 40 },
    ]);
  }
  // Round 3+ opens up ghost.
  if (round === 3) {
    return rollWeighted<AgentArchetype>([
      { value: 'tracker',   weight: 40 },
      { value: 'commander', weight: 40 },
      { value: 'ghost',     weight: 20 },
    ]);
  }
  // Round 4+ even distribution.
  return rollWeighted<AgentArchetype>([
    { value: 'tracker',   weight: 33 },
    { value: 'commander', weight: 34 },
    { value: 'ghost',     weight: 33 },
  ]);
}

// --- Face + codename ---

/** How many face_NN.png assets exist in public/agent_faces/. */
const FACE_COUNT = 35;

/**
 * Two-word call signs in the spirit of "Snake Eater", "Diamond Eye",
 * "Green Dragon", "Goth Chick". Mix of menacing operator-style
 * nicknames and off-kilter/mundane ones so the dossier photo paired
 * with the name feels like an actual person's handle instead of a
 * generic codename generator. Pulled randomly per agent per round.
 */
const CODENAMES = [
  // Menacing / military
  'SNAKE EATER', 'DIAMOND EYE', 'GREEN DRAGON', 'IRON HAND',
  'BLACK MAMBA', 'NIGHT WOLF', 'COLD IRON', 'DEAD EYE',
  'BLOOD MOON', 'SILENT HAWK', 'ICE PHANTOM', 'SMOKE JACKAL',
  'WHITE TIGER', 'DESERT CROW', 'LONE RAVEN', 'FINAL CUT',
  'LAST CALL', 'GRAVE DIGGER', 'WIRE CUTTER', 'GLASS BREAKER',
  // Material / body part combos
  'BRASS KNUCKLE', 'GLASS JAW', 'SILVER TONGUE', 'GOLD TOOTH',
  'WOODEN HEART', 'PAPER FACE', 'GREY MATTER',
  // Off-kilter / mundane (the Goth Chick slot)
  'GOTH CHICK', 'PROM KING', 'SUNDAY DRIVER', 'CHURCH MOUSE',
  'OLD MONEY', 'BAD POSTURE', 'QUIET TYPE', 'FREE SAMPLES',
  'NICE GUY', 'SLOW CLAP',
  // Animal + descriptor
  'MAD HORNET', 'STONE OX', 'RUST HOUND', 'CROOKED FOX',
  'RED HERRING',
];

// --- Public API ---

/**
 * Generate a complete agent loadout for the given round. Called once per
 * round by initializeGameState. Deterministic-random — the same round
 * number won't give the same loadout twice, by design.
 */
export interface GenerateAgentLoadoutOptions {
  /**
   * Force an exact passive count (0 = no passives). Used by the run-path
   * map: Hit nodes pass 0, Elite/Boss nodes pass 2, regardless of what the
   * round-based curve would roll. Omit to fall back to round-based rolling.
   */
  passiveCount?: number;
}

export function generateAgentLoadout(
  round: number,
  usedCodenames: string[] = [],
  options?: GenerateAgentLoadoutOptions,
): AgentLoadout {
  const weaponId = rollWeapon(round);
  // Sniper agents use the tracker archetype (methodical, overwatch-oriented)
  // rather than rolling a random archetype that might conflict with the
  // sniper overwatch behavior.
  const archetype = weaponId === 'sniper-rifle' ? 'tracker' : rollArchetype(round);
  // Avoid reusing codenames within a single run. If the used set has
  // consumed every codename (long run), wrap and allow reuse rather
  // than crashing.
  const used = new Set(usedCodenames);
  const available = CODENAMES.filter(c => !used.has(c));
  const pool = available.length > 0 ? available : CODENAMES;
  const codename = pool[Math.floor(Math.random() * pool.length)];
  const passives = options?.passiveCount !== undefined
    ? pickPassivesFromPool(round, options.passiveCount)
    : rollPassives(round);
  return {
    archetype,
    weaponId,
    defensiveItemIds: rollDefensiveItems(round),
    passives,
    faceId: 1 + Math.floor(Math.random() * FACE_COUNT),
    codename,
  };
}
